import { Box, Paper } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { PreviewOff } from '.';
import { claimPageUrl } from '../../../../../config/app';
import { BASE_SCALE, MOBILE_VIEW_WIDTH } from '../../../../../constants/templates';
import { ClaimLinkEditorViewTypeEnum, IframeStatusEnum } from '../../../../../types/templates';
import Loader from '../../../../Loader/Loader';

// Delay revealing the iframe after the handshake completes, so the claim page has
// applied the init/experience data and won't flash its un-hydrated default state
// (placeholder header + blank body) for a frame.
const PREVIEW_REVEAL_DELAY_MS = 400;

interface IPreviewProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  viewType: ClaimLinkEditorViewTypeEnum;
  zoom: number;
  status: IframeStatusEnum;
  onLoad: () => void;
  onError: () => void;
  onRetry: () => void;
  onOpenInNewTab: () => void;
}

export const Preview: React.FC<IPreviewProps> = ({
  iframeRef,
  viewType,
  zoom,
  status,
  onLoad,
  onError,
  onRetry,
  onOpenInNewTab,
}) => {
  const isMobile = useMemo(() => viewType === ClaimLinkEditorViewTypeEnum.Mobile, [viewType]);
  const scale = useMemo(() => BASE_SCALE * (zoom / 100), [zoom]);

  // Gate the reveal on a short settle after Ready so init/experience data paints
  // first; drop back to the loader immediately if we leave the Ready state.
  const [isRevealed, setIsRevealed] = useState<boolean>(false);

  useEffect(() => {
    if (status !== IframeStatusEnum.Ready) {
      setIsRevealed(false);
      return;
    }

    const timer = setTimeout(() => setIsRevealed(true), PREVIEW_REVEAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const overlay = useMemo(() => {
    if (status === IframeStatusEnum.Error) {
      return <PreviewOff onRetry={onRetry} onOpenInNewTab={onOpenInNewTab} />;
    }
    if (status === IframeStatusEnum.Loading || !isRevealed) {
      return <Loader isLoading={true} />;
    }
    return null;
  }, [status, isRevealed, onRetry, onOpenInNewTab]);

  const showIframe = useMemo(() => {
    return status === IframeStatusEnum.Ready && isRevealed;
  }, [status, isRevealed]);

  return (
    <Paper
      sx={{
        width: isMobile ? MOBILE_VIEW_WIDTH : '100%',
        height: '100%',
        backgroundColor: 'grey.50',
        overflow: 'auto',
        position: 'relative',
        ...(isMobile && { margin: '0 auto' }),
      }}
    >
      {overlay && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            // Opaque so the iframe's un-hydrated default state (blank body,
            // placeholder header) can't flash through while it connects/paints.
            backgroundColor: 'grey.50',
          }}
        >
          {overlay}
        </Box>
      )}
      <Box
        sx={{
          width: `${scale * 100}%`,
          height: `${scale * 100}%`,
          minWidth: '100%',
          minHeight: '100%',
          position: 'relative',
          visibility: showIframe ? 'visible' : 'hidden',
        }}
      >
        <iframe
          ref={iframeRef}
          src={`${claimPageUrl}/iframe`}
          style={{
            position: 'absolute',
            width: `${100 / scale}%`,
            height: `${100 / scale}%`,
            border: 'none',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
          title="Preview"
          onLoad={onLoad}
          onError={onError}
          sandbox="allow-scripts allow-same-origin allow-forms"
        />
      </Box>
    </Paper>
  );
};
